/* eslint-disable no-underscore-dangle,@typescript-eslint/no-unused-vars */
import React, { MutableRefObject, useRef, useState, useMemo } from 'react';
import { useQueryParam } from 'use-query-params';
import { toast } from 'react-toastify';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import { useAudiencesLoader } from '@/features/editor/use-editor-loader';
import { maybe } from '@/features/details/utils';
import { useAudienceQueryBuilder } from '@/features/editor/widgets/shared/modals/use-audience-query-builder';
import { QBItemProp, QBItemSelection } from '@/components/query-builder/models';
import { useAudiencesApi } from '@/features/dashboard/audiences/api';
import { Audience, AudienceType } from '@/features/dashboard/audiences/models';
import {
  Placement,
  PlacementKind,
  Targeting,
} from '@/webapi/use-experience-api';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { SharedElementOverlayProps } from '@/components/shared-element-overlay';
import {
  DateRangeParam,
  EndDateParameter,
  StartDateParameter,
} from '@/features/dashboard/shared/date-picker/date-picker';
import { DateRange } from '@/features/dashboard/reports/models';
import { calcStartAndEndDates } from '@/features/dashboard/audiences/dateUtil';

export interface AudiencesContext {
  loading: boolean;
  onSave: () => void;
  onToggle: (audience: Audience) => Promise<void>;
  audienceProps: QBItemProp[];
  audience: QBItemSelection[];
  onQueryBuilderChange: (v: QBItemSelection[]) => void;
  nameStep: boolean;
  setNameStep: (b: boolean) => void;
  name: string;
  setName: (s: string) => void;
  description: string;
  setDescription: (s: string) => void;
  isModalVisible: boolean;
  onDelete: (audience: Audience) => void;
  setIsModalVisible: (b: boolean) => void;
  audiencesList: Array<Audience>;
  sharedOverlayProps: SharedElementOverlayProps;
  audienceToEdit: Audience | undefined;
  showConfirmModal: () => void;
  hideConfirmModal: () => void;
  confirmModalRef: MutableRefObject<any>;
  audienceDelete: () => Promise<void>;
  setAudienceToDelete: (a: Audience) => void;
  onEdit: (audience: Audience) => void;
  searchQuery: string;
  setSearchQuery: (s: string) => void;
  sortBy: string;
  setSortBy: (s: string) => void;
  setDateRange: (d: DateRange) => void;
  setStartDate: (d: Date) => void;
  setEndDate: (d: Date) => void;
  dateRange: DateRange;
  startDate: Date;
  endDate: Date;
}

export const AudiencesContext = React.createContext({} as AudiencesContext);

export function newAudiencesCtx(): AudiencesContext {
  const { resources, loading: loaderLoading } = useAudiencesLoader();
  const props = maybe(() => useAudienceQueryBuilder(resources));
  const [audience, setAudience] = useState<QBItemSelection[]>([]);

  const [nameStep, setNameStep] = useState(false);
  const [name, setName] = useState(``);
  const [description, setDescription] = useState(``);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [rawAudiences, setRawAudiences] = useState<Array<Audience>>([]);
  const [audienceToDelete, setAudienceToDelete] = useState<Audience>(null);
  const [audienceToEdit, setAudienceToEdit] = useState<Audience>(null);
  const [dateRange] = useQueryParam<DateRange>(`rn`, DateRangeParam);
  const [startDate] = useQueryParam(`s`, StartDateParameter);
  const [endDate] = useQueryParam(`e`, EndDateParameter);

  const dateRangeSignal = useSignal(dateRange);
  const startDateSignal = useSignal(startDate);
  const endDateSignal = useSignal(endDate);

  const [currentDateRange, setCurrentDateRange] = useState(dateRange);
  const [currentStartDate, setCurrentStartDate] = useState(startDate);
  const [currentEndDate, setCurrentEndDate] = useState(endDate);

  useSignalEffect(() => {
    setCurrentDateRange(dateRangeSignal.value);
    setCurrentStartDate(startDateSignal.value);
    setCurrentEndDate(endDateSignal.value);
  });

  const setDateRange = (d: DateRange) => {
    dateRangeSignal.value = d;
  };
  const setStartDate = (d: Date) => {
    startDateSignal.value = d;
  };
  const setEndDate = (d: Date) => {
    endDateSignal.value = d;
  };

  const [searchQuery, setSearchQuery] = useState(``);
  const [sortBy, setSortBy] = useState<string>(`CREATION_DATE`);

  const {
    loading,
    loadingPrimaryAudiencesSet,
    createAudience,
    listAudiences,
    deleteAudience,
    updateAudience,
    toggleAudience,
  } = useAudiencesApi(true);

  const initAudiences = async () => {
    const { start, end } = calcStartAndEndDates(
      dateRangeSignal.value,
      startDateSignal.value,
      endDateSignal.value,
    );

    const response = await listAudiences(start, end);
    if (response?.audiences?.length > 0) {
      setRawAudiences(response.audiences);
    } else {
      setRawAudiences([]);
    }
  };

  useSignalEffect(() => {
    const _d = dateRangeSignal.value;
    const _s = startDateSignal.value;
    const _e = endDateSignal.value;
    initAudiences();
  });

  const audiencesList = useMemo(() => {
    let result = [...rawAudiences];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((a) => a.name?.toLowerCase().includes(q));
    }

    result.sort((a, b) => {
      if (sortBy === `CREATION_DATE_ASC`) {
        const dateA = parseInt(a.createdAt || `0`, 10);
        const dateB = parseInt(b.createdAt || `0`, 10);
        return dateB - dateA;
      }
      if (sortBy === `CREATION_DATE_DSC`) {
        const dateA = parseInt(a.createdAt || `0`, 10);
        const dateB = parseInt(b.createdAt || `0`, 10);
        return dateA - dateB;
      }

      const getVal = (aud: Audience) =>
        aud.metrics?.find((m) => m.name === sortBy)?.value || 0;

      return getVal(b) - getVal(a);
    });

    return result;
  }, [rawAudiences, searchQuery, sortBy]);

  const onSave = async () => {
    if (!nameStep) {
      setNameStep(true);
      if (audienceToEdit?.connectedExperiences) {
        setName(``);
        setDescription(``);
      }
      return;
    }
    if (audienceToEdit && !audienceToEdit?.connectedExperiences) {
      const toUpdate = makeAudienceObject(name, description, audience);
      toUpdate.id = audienceToEdit.id;
      toUpdate.experienceId = audienceToEdit.experienceId;
      await updateAudience(toUpdate);
      setAudienceToEdit(null);
    } else {
      await createAudience(makeAudienceObject(name, description, audience));
    }
    setIsModalVisible(false);
    setName(``);
    setDescription(``);
    setNameStep(false);
    setAudience([]);
    await initAudiences();
  };

  const onEdit = (audience: Audience) => {
    setDescription(audience.description);
    setName(audience.name);
    setAudience(audience?.targeting?.other);
    setAudienceToEdit(audience);
    setNameStep(false);
    setIsModalVisible(true);
  };

  const onDelete = (audience: Audience) => {
    showConfirmModal();
    setAudienceToDelete(audience);
  };

  const onQueryBuilderChange = (value: QBItemSelection[]) => {
    setAudience(value);
  };

  const {
    confirmModalRef,
    sharedOverlayProps,
    showConfirmModal,
    hideConfirmModal,
  } = confirmModalProps();

  const audienceDelete = async () => {
    await deleteAudience(audienceToDelete);
    setAudienceToDelete(null);
    hideConfirmModal();
    await initAudiences();
  };

  const hideModal = (b: boolean) => {
    if (!b) {
      setDescription(``);
      setName(``);
      setAudience([]);
      setAudienceToEdit(null);
      setNameStep(false);
    }
    setIsModalVisible(b);
  };
  const onToggle = async (audience: Audience) => {
    if (loadingPrimaryAudiencesSet) {
      return;
    }
    const strings = rawAudiences.filter((a) => a.isPrimary).map((x) => x.id);
    const uniquePrimary = new Set([...strings, audience.id]);
    if (uniquePrimary.size > 4) {
      toast(
        `Up to 4 primary audiences can be set. Please remove one of the existing primary audiences before adding a new one.`,
        {
          theme: `colored`,
          type: `info`,
        },
      );
      return;
    }
    const next = {
      ...audience,
      isPrimary: !audience.isPrimary,
    };
    await toggleAudience(next as Audience);
    setRawAudiences(rawAudiences.map((a) => (a.id === audience.id ? next : a)));
  };
  return {
    onSave,
    onToggle,
    onQueryBuilderChange,
    loading: loaderLoading || loading,
    audienceProps: props?.audienceProps || [],
    audience,
    nameStep,
    setNameStep,
    name,
    setName,
    description,
    setDescription,
    isModalVisible,
    onDelete,
    setIsModalVisible: hideModal,
    audiencesList,
    sharedOverlayProps,
    audienceToEdit,
    showConfirmModal: () => {
      setAudienceToDelete(null);
      showConfirmModal();
    },
    hideConfirmModal: () => {
      setAudienceToDelete(null);
      hideConfirmModal();
    },
    confirmModalRef,
    audienceDelete,
    setAudienceToDelete,
    onEdit,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    setDateRange,
    setStartDate,
    setEndDate,
    dateRange: currentDateRange,
    startDate: currentStartDate,
    endDate: currentEndDate,
  };
}

function confirmModalProps() {
  const confirmModalRef = useRef(null);

  const {
    props: sharedOverlayProps,
    show: showConfirmModal,
    hide: hideConfirmModal,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#a6afb8`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    confirmModalRef,
    () => centered(19, 42),
  );
  return {
    confirmModalRef,
    sharedOverlayProps,
    showConfirmModal,
    hideConfirmModal,
  };
}

export function makeAudienceObject(
  name: string,
  description: string,
  audience: QBItemSelection[],
) {
  return {
    name,
    description,
    type: AudienceType.CUSTOM_AUDIENCE_TYPE,
    targeting: {
      segments: [],
      placement: {
        kind: PlacementKind.ALL_PAGES,
      } as Placement,
      other: audience,
    } as Targeting,
  } as Audience;
}
